"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { ApiService } from "@/app/services/AppService";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { BannerMobile, ResponseObject } from "@/app/classes/main";
import { useRouter } from "next/navigation";
import { Loader } from "../genericComponents/MainLoader/Loader";
import "./navbarMobileBanners.css";
import ScannerAuth from "../genericComponents/scanner-auth/ScannerAuth";
import { IconButton } from "@mui/material";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
interface NavbarMobileBannersProps {
  setReloadScrollFlag: (flag: boolean) => void;
  setReloadSearchPositionFlag: (flag: boolean) => void;
  reloadNavBackground: any;
}

interface SubCategory {
  id: number;
  slug: string; // Add all other fields that are relevant
}

interface Categories {
  id: number;
  slug: string; // Add all other fields that are relevant
}

export const NavbarMobileBanners: React.FC<NavbarMobileBannersProps> = ({
  setReloadScrollFlag,
  setReloadSearchPositionFlag,
  reloadNavBackground,
}) => {
  const [banner, setBanner] = useState<BannerMobile[]>([]);
  const [categories, setCategories] = useState<Categories[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[] | null>(
    null
  );
  const [opacity, setOpacity] = useState(1);
  const [loader, setLoader] = useState(false);
  const [serviceHubSlug, setServiceHubSlug] = useState("noida");
  const [openInfo, setOpenInfo] = useState(false);
  const [slug, setSlug] = useState<undefined | string | null>(null);
  const [bannerId, setBannerId] = useState<undefined | number>();
  const [isMuted, setIsMuted] = useState(true);
  const xHubId = getHubId();
  const router = useRouter();
  const apiService = useMemo(() => new ApiService(), []);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    try {
      const storedSlug = localStorage.getItem("serviceHubSlug");
      if (storedSlug) {
        const decodedSlug = JSON.parse(atob(storedSlug));
        if (decodedSlug) {
          setServiceHubSlug(decodedSlug || "noida");
        }
      }
    } catch (error) {
      console.error("Error decoding or parsing serviceHubSlug:", error);
    }
  }, []);

  const getDataMobileBanners = useMemo(
    () => async () => {
      try {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.headerBannerMobile + "?hubId=" + xHubId
          );
        const categories: ResponseObject | undefined = (
          await apiService.get<any>(
            apiService.categories + "?hubSlug=" + serviceHubSlug
          )
        ).object;
        const response: ResponseObject | undefined = await apiService.get<any>(
          `${apiService.subcategories}?hubSlug=${serviceHubSlug}&categorySlug=salon-at-home`
        );
        setSubcategories(response?.object || []);
        setCategories(categories?.object || []);
        setBanner(responseObject?.object || []);
      } catch (error) {
        console.log("Error fetching data:", error);
      }
    },
    [apiService, xHubId, serviceHubSlug]
  );

  useEffect(() => {
    getDataMobileBanners();
  }, [getDataMobileBanners]);

  const updatedBannersArray = useMemo(
    () =>
      banner?.map((item: any) => {
        const matchedCategory = categories?.find(
          (category: any) => category.id === item.categoryId
        );

        const matchedSubCategory = subcategories?.find(
          (subCategory: any) => subCategory.id === item.subCategoryId
        );

        return {
          ...item,
          slug: matchedCategory ? matchedCategory?.slug : "salon-at-home",
          subSlug: matchedSubCategory ? matchedSubCategory?.slug : null,
        };
      }),
    [banner, categories, subcategories]
  );

  const slideCount = updatedBannersArray?.length ?? 0;
  const settings = useMemo(
    () => ({
      dots: slideCount > 1,
      infinite: slideCount > 1,
      arrows: false,
      speed: 450,
      slidesToShow: 1,
      slidesToScroll: 1,
      swipe: true,
      touchThreshold: 8,
      className: "myCustomCarouselBannerMobileView",
      afterChange: (index: number) => setCurrentSlide(index),
    }),
    [slideCount]
  );

  useEffect(() => {
    const initialScrollY = window.scrollY;
    const initialOpacity = Math.max(0.1, 1 - initialScrollY / 200);
    const initialOpacityValue = parseFloat(initialOpacity.toFixed(1));
    setOpacity(initialOpacityValue);
    if (initialOpacityValue <= 0.9) {
      setReloadSearchPositionFlag(true);
    }
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const newOpacity = Math.max(0.1, 1 - scrollY / 200);
      const roundedOpacity = parseFloat(newOpacity.toFixed(1));
      setOpacity((prevOpacity) =>
        prevOpacity !== roundedOpacity ? roundedOpacity : prevOpacity
      );

      if (roundedOpacity <= 0.6) {
        setReloadScrollFlag(true);
      } else if (roundedOpacity <= 0.9) {
        setReloadSearchPositionFlag(true);
      } else {
        setReloadScrollFlag(false);
        setReloadSearchPositionFlag(false);
      }
      reloadNavBackground(roundedOpacity);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [setReloadScrollFlag, setReloadSearchPositionFlag, reloadNavBackground]);

  useEffect(() => {
    if (opacity >= 0.7) return;
    videoRefs.current.forEach((v) => {
      if (v) v.pause();
    });
    setIsMuted(true);
  }, [opacity]);

  /** Only the active slide’s video should play; others stay paused. */
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === currentSlide && opacity >= 0.7) {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [currentSlide, opacity]);

  const handleMobileImage = (item: any) => {
    const { slug, subSlug, bannerTypeCode, pageContentId } = item;
    if (bannerTypeCode == 5) {
      setOpenInfo(true);
      setBannerId(pageContentId);
      setSlug(slug);
    } else {
      setLoader(true);
      subSlug
        ? router.push(`${serviceHubSlug}/${slug}/${subSlug}`)
        : router.push(`${serviceHubSlug}/${slug}`);
    }
  };

  const handleMuteUnmute = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRefs.current[currentSlide];
    if (v) {
      if (v.muted) {
        v.muted = false;
        setIsMuted(false);
      } else {
        v.muted = true;
        setIsMuted(true);
      }
    }
  };

  return (
    <>
      <div className="navbar-mobile-banners-root">
        <Slider {...settings}>
          {updatedBannersArray &&
            updatedBannersArray.map((item, index) => (
              <div
                key={
                  item?.pageContentId ?? item?.id ?? `banner-slide-${index}`
                }
              >
                <div
                  className="navbar-mobile-banners-slide"
                  onClick={() => handleMobileImage(item)}
                  role="presentation"
                >
                  {item?.mobileImage?.secureHttpPath?.endsWith(".mp4") ||
                  item?.mobileImage?.secureHttpPath?.endsWith(".webm") ? (
                    <>
                      <video
                        ref={(el) => {
                          videoRefs.current[index] = el;
                        }}
                        style={{ opacity }}
                        className="navbar-mobile-banners-video"
                        autoPlay
                        loop
                        muted={isMuted}
                        playsInline
                        onContextMenu={(e) => e.preventDefault()}
                        disablePictureInPicture
                        controlsList="nodownload noremoteplayback noplaybackrate"
                        controls={false}
                        preload={index === currentSlide ? "auto" : "metadata"}
                      >
                        <source
                          src={item?.mobileImage?.secureHttpPath}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                      {currentSlide === index &&
                        (item?.mobileImage?.secureHttpPath?.endsWith(
                          ".mp4"
                        ) ||
                          item?.mobileImage?.secureHttpPath?.endsWith(
                            ".webm"
                          )) && (
                          <div className="navbar-mobile-banners-mute-wrap">
                            <IconButton
                              onClick={handleMuteUnmute}
                              className="navbar-mobile-banners-mute-btn"
                              aria-label={
                                isMuted ? "Unmute video" : "Mute video"
                              }
                              size="small"
                            >
                              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </IconButton>
                          </div>
                        )}
                    </>
                  ) : (
                    <img
                      src={item?.mobileImage?.secureHttpPath}
                      alt={
                        (item as { heading?: string })?.heading ||
                        "Promotional banner"
                      }
                      draggable={false}
                      className="navbar-mobile-banners-img"
                      style={{ opacity }}
                    />
                  )}
                  <div className="navbar-mobile-banners-fade" />
                </div>
              </div>
            ))}
        </Slider>
      </div>
      {loader && <Loader />}
      <ScannerAuth
        openInfo={openInfo}
        handleClose={() => setOpenInfo(false)}
        id={bannerId}
        slug={slug}
        serviceHubSlug={serviceHubSlug}
      />
    </>
  );
};

export default NavbarMobileBanners;
